import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { setApiContext } from '../api';
import { useAuth } from './AuthContext';

const SocieteContext = createContext(null);

const STORAGE_KEY = 'mbud_societe_context';

export function SocieteProvider({ children }) {
  const { user } = useAuth();
  // ── State ──────────────────────────────────────────────────────────────────
  const [societes, setSocietes] = useState([]);         // toutes les sociétés de l'org
  const [activeSociete, setActiveSociete] = useState(null);  // société active { societe_id, nom, agences }
  const [activeAgences, setActiveAgences] = useState([]);    // agences actives [{ agence_id, nom }]
  const [loading, setLoading] = useState(true);

  // ── Persistance ────────────────────────────────────────────────────────────
  const persist = (societe, agences) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        societe_id: societe?.societe_id || null,
        agences_ids: agences.map(a => a.agence_id),
      }));
    } catch {}
  };

  // ── Sync contexte → Axios headers ─────────────────────────────────────────
  useEffect(() => {
    setApiContext(
      activeSociete?.societe_id || null,
      activeAgences.map(a => a.agence_id),
    );
  }, [activeSociete, activeAgences]);

  // ── Charger les sociétés de l'org ─────────────────────────────────────────
  const loadSocietes = useCallback(async () => {
    setLoading(true);
    try {
      // Import dynamique pour éviter la dépendance circulaire
      const api = await import('../api');
      const res = await api.getSocietes();
      const list = (Array.isArray(res.data) ? res.data : [])
        .sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' }))
        .map(soc => ({
          ...soc,
          agences: (soc.agences || []).sort((a, b) =>
            (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' })
          ),
        }));
      setSocietes(list);

      // Restaurer la sélection sauvegardée
      let restored = false;
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        if (saved.societe_id) {
          const soc = list.find(s => s.societe_id === saved.societe_id);
          if (soc) {
            setActiveSociete(soc);
            const ags = (soc.agences || []).filter(a => (saved.agences_ids || []).includes(a.agence_id));
            setActiveAgences(ags);
            restored = true;
          }
        }
      } catch {}

      // Auto-sélection : si l'utilisateur n'a accès qu'à une seule société
      // et qu'aucune préférence n'a été restaurée depuis localStorage
      if (!restored && list.length === 1) {
        const soc = list[0];
        setActiveSociete(soc);
        const agences = soc.agences || [];
        if (agences.length === 1) {
          setActiveAgences(agences);
          persist(soc, agences);
        } else {
          setActiveAgences([]);
          persist(soc, []);
        }
      }
    } catch {}
    finally { setLoading(false); }
  }, []);

  // Recharger quand l'utilisateur change (ex: déconnexion/reconnexion dans le même onglet)
  useEffect(() => {
    if (user) {
      loadSocietes();
    } else {
      // Réinitialiser quand l'utilisateur se déconnecte
      setSocietes([]);
      setActiveSociete(null);
      setActiveAgences([]);
      setLoading(false);
    }
  }, [loadSocietes, user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Actions ────────────────────────────────────────────────────────────────
  const selectSocieteWithAgences = (societe, agences) => {
    setActiveSociete(societe);
    setActiveAgences(agences);
    persist(societe, agences);
  };

  const selectSociete = (societe) => {
    setActiveSociete(societe);
    setActiveAgences([]);  // reset agences quand on change de société
    persist(societe, []);
  };

  const clearSociete = () => {
    setActiveSociete(null);
    setActiveAgences([]);
    persist(null, []);
  };

  const toggleAgence = (agence) => {
    setActiveAgences(prev => {
      const exists = prev.some(a => a.agence_id === agence.agence_id);
      const next = exists
        ? prev.filter(a => a.agence_id !== agence.agence_id)
        : [...prev, agence];
      persist(activeSociete, next);
      return next;
    });
  };

  const selectAllAgences = () => {
    const all = activeSociete?.agences || [];
    setActiveAgences(all);
    persist(activeSociete, all);
  };

  const clearAgences = () => {
    setActiveAgences([]);
    persist(activeSociete, []);
  };

  // Mode lecture seule :
  // - aucune société sélectionnée (vue "toutes les sociétés")
  // - société sélectionnée sans agence spécifique (0 agences = vue agrégée)
  // - société sélectionnée avec plusieurs agences (2+ = ambiguïté)
  // → seul le cas "1 agence précise" autorise la création/modification
  const isReadOnly = activeSociete === null || activeAgences.length !== 1;

  // Fuseau horaire de la société active (défaut métropole)
  const activeSocieteTimezone = activeSociete?.timezone || 'Europe/Paris';

  return (
    <SocieteContext.Provider value={{
      societes,
      activeSociete,
      activeAgences,
      isReadOnly,
      loading,
      activeSocieteTimezone,
      selectSociete,
      clearSociete,
      selectSocieteWithAgences,
      toggleAgence,
      selectAllAgences,
      clearAgences,
      reload: loadSocietes,
    }}>
      {children}
    </SocieteContext.Provider>
  );
}

export const useSociete = () => {
  const ctx = useContext(SocieteContext);
  if (!ctx) throw new Error('useSociete doit être utilisé dans SocieteProvider');
  return ctx;
};
